import type { HTMLAttributes, ReactNode } from 'react'

import { Icon } from './Icon'
import styles from './CreativeCard.module.css'

type CreativeKind = 'video' | 'image'
type CreativeSource = 'ai' | 'upload' | 'kol'
interface CreativeCompliance {
  level: 'violation' | 'risk'
  reason: string
}

const sourceLabels: Record<CreativeSource, string> = {
  ai: 'AI 生成',
  upload: '我的上传',
  kol: '达人授权',
}

export interface CreativeCardProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  name: string
  kind: CreativeKind
  cover: string
  dataUrl?: string
  duration?: string | null
  source: CreativeSource
  kol?: string | null
  usedByAds?: number
  compliance?: CreativeCompliance | null
  actions?: ReactNode
  showUsage?: boolean
  showKindBadge?: boolean
  dimWhenUnused?: boolean
}

function assetUrl(path: string): string {
  if (!path || path.startsWith('/') || path.startsWith('data:')) return path
  return `/${path}`
}

export function CreativeCard({
  name,
  kind,
  cover,
  dataUrl,
  duration = null,
  source,
  kol = null,
  usedByAds = 0,
  compliance = null,
  actions,
  showUsage = true,
  showKindBadge = true,
  dimWhenUnused = false,
  className = '',
  ...props
}: CreativeCardProps) {
  const image = dataUrl || assetUrl(cover)
  const complianceClass = compliance?.level === 'violation' ? styles.violation : styles.risk
  return (
    <div data-component="creative-card" className={`${styles.card} ${dimWhenUnused && usedByAds === 0 ? styles.unused : ''} ${className}`} {...props}>
      <div className={styles.figure}>
        <img src={image} alt={name} />
        {kind === 'video' ? (
          <span className={styles.mediaBadge}><Icon name="play" size={12} strokeWidth={2} />{duration}</span>
        ) : showKindBadge ? <span className={styles.kindBadge}>图片</span> : null}
        {compliance ? (
          <span className={`${styles.flag} ${complianceClass}`} title={compliance.reason}>
            <Icon name="alert" size={13} strokeWidth={2.2} />
            {compliance.level === 'violation' ? '违规' : '风险'}
          </span>
        ) : null}
        {actions ? <div className={styles.hoverActions}>{actions}</div> : null}
      </div>
      <div className={styles.body}>
        <p className={styles.name}>{name}</p>
        {!showUsage && !(source === 'kol' && kol) ? (
          <span className={`${styles.source} ${styles[source]}`}>{sourceLabels[source]}</span>
        ) : (
          <div className={styles.meta}>
            <span className={`${styles.source} ${styles[source]}`}>{sourceLabels[source]}</span>
            {source === 'kol' && kol ? <span className={styles.kolMeta}>{kol}</span> : null}
          </div>
        )}
        {showUsage ? <p className={`${styles.usage} ${usedByAds === 0 ? styles.usageOff : ''}`}>{usedByAds > 0 ? `${usedByAds} 个广告在用` : '未使用'}</p> : null}
      </div>
    </div>
  )
}
